import {
  Injectable,
  ComponentRef,
  ApplicationRef,
  Type,
  createComponent,
  EnvironmentInjector,
  EventEmitter,
} from '@angular/core';
import { Subject } from 'rxjs';
import { DomListener } from 'src/app/utilities/DomListener';
import { DomListenerFactoryService } from './dom-listener-factory.service';

export interface FullScreenModalConfig {
  /** Optional data to pass to the modal component instance. */
  data?: any;
}

export interface FullScreenModalRef<T = any> {
  /** The instance of the component opened in the modal. */
  componentInstance: T;
  /** A promise that resolves with the result when the modal is closed. */
  afterClosed: () => Promise<any>;
}

/**
 * A service for creating truly non-dismissible, full-screen modals.
 * This service is designed for critical UI flows (like payment or initial setup)
 * where the user MUST complete an action before proceeding. It bypasses Angular Material's
 * standard dialog system to prevent dismissal from system events like minimizing the
 * browser or focus loss. The Escape key is always blocked.
 */
@Injectable({
  providedIn: 'root',
})
export class FullScreenModalService {
  /** The DOM container element that hosts the modal component and covers the full viewport.
   *  Created dynamically with full-screen styles, attached to document.body, and removed on close. */
  private modalContainerElement: HTMLElement | null = null;

  /** Angular component reference for the currently active modal - used for lifecycle management.
   *  Handles component creation, data injection, view attachment, and cleanup on destruction. */
  private currentModalRef: ComponentRef<any> | null = null;

  /** RxJS Subject that resolves the afterClosed() promise when the modal is dismissed.
   *  Created per modal instance, emits the close result, and completes when modal closes. */
  private currentCloseSubject: Subject<any> | null = null;

  /** Manages global event listeners, particularly for blocking Escape key dismissal.
   *  Prevents modal dismissal events and is cleared when modal closes to avoid memory leaks. */
  private domListener: DomListener;

  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    private domListenerFactory: DomListenerFactoryService,
  ) {
    this.domListener = this.domListenerFactory.createInstance();
  }

  /**
   * Opens a full-screen, non-dismissible modal.
   * @param component The component type to render within the modal.
   * @param config The configuration for the modal.
   * @returns A reference to the newly created modal.
   */
  open<T>(component: Type<T>, config: FullScreenModalConfig = {}): FullScreenModalRef<T> {
    if (this.currentModalRef) {
      this.close(); // Ensure only one modal is open at a time.
    }

    this.currentCloseSubject = new Subject<any>();

    // 1. Create the Host DOM Element
    // This is the container that will hold our Angular component.
    // It's styled to cover the entire viewport and sit on top of everything else.
    this.modalContainerElement = document.createElement('div');
    this.modalContainerElement.className = 'full-screen-modal-host-container';

    // 2. Create and Attach the Angular Component using the modern API
    // We use createComponent, which does not require a ComponentFactoryResolver.
    const componentRef = createComponent(component, {
      environmentInjector: this.environmentInjector,
      // We specify the host element, so the component is rendered inside our container.
      hostElement: this.modalContainerElement,
    });

    // Manually attach the view to the application ref to make it part of the
    // change detection tree and to make dependency injection work.
    this.appRef.attachView(componentRef.hostView);

    // Inject data and the close handler into the component instance.
    if (config.data) {
      Object.assign(componentRef.instance as any, config.data);
    }
    const instance = componentRef.instance as { closeFullScreenModal?: EventEmitter<any> };
    instance.closeFullScreenModal?.subscribe((result) => this.close(result));

    // 3. Attach to DOM
    document.body.appendChild(this.modalContainerElement);
    this.currentModalRef = componentRef;

    // 4. Set up Blocking Behavior
    this.preventBodyInteraction();
    this.preventDismissalEvents();

    // 5. Create and Return the Modal Reference
    const closeSubject = this.currentCloseSubject;
    const modalRef: FullScreenModalRef<T> = {
      componentInstance: componentRef.instance,
      afterClosed: () =>
        new Promise((resolve) => {
          closeSubject.subscribe({
            next: (result) => resolve(result),
            error: () => resolve(undefined),
          });
        }),
    };

    return modalRef;
  }

  /**
   * Sets up a global event listener to prevent the modal from being dismissed
   * by the Escape key. This is the core of the "non-dismissible" logic.
   */
  private preventDismissalEvents(): void {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    this.domListener.add(document, 'keydown', handleKeyDown, true);
  }

  /**
   * Locks the body to prevent scrolling and interaction with the underlying page.
   */
  private preventBodyInteraction(): void {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed'; // Prevents scroll jumping
    document.body.style.width = '100%';
  }

  /**
   * Restores the body's normal scrolling and interaction behavior.
   */
  private restoreBodyInteraction(): void {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }

  /**
   * Closes the modal and cleans up all resources.
   * @param result Optional data to pass back to the `afterClosed` promise.
   */
  close(result?: any): void {
    this.domListener.clear();

    if (this.currentModalRef) {
      this.appRef.detachView(this.currentModalRef.hostView);
      this.currentModalRef.destroy();
      this.currentModalRef = null;
    }

    if (this.modalContainerElement?.parentElement) {
      this.modalContainerElement.parentElement.removeChild(this.modalContainerElement);
      this.modalContainerElement = null;
    }

    this.restoreBodyInteraction();

    if (this.currentCloseSubject) {
      this.currentCloseSubject.next(result);
      this.currentCloseSubject.complete();
      this.currentCloseSubject = null;
    }
  }
}
